import * as React from 'react';
import { A, Hooks } from 'frontend';
import { formatBytes } from 'frontend/utils';
import {
  id3FrameLabels,
  id3FrameTooltips,
  sortTags,
  type TrackTagsLoadState,
} from 'frontend/logic/music/metadata';

interface Props {
  tagsState: TrackTagsLoadState;
}

function base64ByteLength(base64: string): number {
  let padding = 0;
  if (base64.endsWith('==')) {
    padding = 2;
  } else if (base64.endsWith('=')) {
    padding = 1;
  }
  return (base64.length / 4) * 3 - padding;
}

export function TagsTab({ tagsState }: Props) {
  const dispatch = Hooks.useDispatch();

  if (tagsState.status === 'loading') {
    return (
      <div className="editTrackModalTags">
        <div className="editTrackModalTagsLoading">Loading…</div>
      </div>
    );
  }

  if (tagsState.status === 'error') {
    return (
      <div className="editTrackModalTags">
        <div className="editTrackModalTagsLoading">
          Error: {tagsState.message}
        </div>
      </div>
    );
  }

  const { blocks } = tagsState.data;

  if (blocks.length === 0 || blocks.every((block) => block.tags.length === 0)) {
    return (
      <div className="editTrackModalTags">
        <div className="editTrackModalTagsLoading">No tags found</div>
      </div>
    );
  }

  return (
    <div className="editTrackModalTags">
      {blocks.map(({ format, tags }) =>
        tags.length === 0 ? null : (
          <div key={format}>
            <div className="editTrackModalTagsFormat">{format}</div>
            <div className="editTrackModalGrid">
              {sortTags(tags).map(({ id, value, binary }, i) => {
                const label =
                  id3FrameLabels[id as keyof typeof id3FrameLabels] ?? id;
                const tooltip =
                  id3FrameTooltips[id as keyof typeof id3FrameTooltips];
                const title = tooltip ? `${id} – ${tooltip}` : id;
                return (
                  <div key={i} className="editTrackModalRow">
                    <span className="editTrackModalLabel" title={title}>
                      {label}
                    </span>
                    {binary !== undefined ? (
                      <div className="editTrackModalTagBinaryCell">
                        <input
                          className="editTrackModalInput editTrackModalInput-readonly editTrackModalInput-binary"
                          value={value}
                          title={title}
                          readOnly
                        />
                        <button
                          className="button editTrackModalTagLogButton"
                          onClick={() => {
                            const bytes = Uint8Array.from(atob(binary), (c) =>
                              c.charCodeAt(0),
                            );
                            console.log(
                              `[Tags] ${format} / ${id}:`,
                              bytes.buffer,
                            );
                            dispatch(
                              A.addMessage({
                                message: `Logged ${id} to console (${formatBytes(bytes.byteLength)})`,
                                timeout: true,
                              }),
                            );
                          }}
                        >
                          {formatBytes(base64ByteLength(binary))}
                        </button>
                      </div>
                    ) : (
                      <input
                        className="editTrackModalInput editTrackModalInput-readonly"
                        value={value}
                        readOnly
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ),
      )}
    </div>
  );
}
